import { CssBaseline, ThemeProvider } from '@mui/material';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { buildTheme, THEME_MODES, type ThemeMode } from './theme';

/**
 * Which theme the panel wears, and who decides.
 *
 * Three choices, two themes: light, dark, and following the machine. **Dark is
 * the default**, at the owner's request — the panel is looked at all day in a
 * depot office, and that is the call of the person who has to look at it.
 * Following the machine is still there for anyone who wants it, and is still
 * the better answer in the abstract: a warehouse office at seven in the morning
 * and the same office at seven in the evening are different rooms, and the
 * operating system already knows which one it is. It is just not what was
 * asked for.
 *
 * The choice is this browser's, in `localStorage` beside the language, not
 * the account's. It is a property of the screen being looked at: the same
 * administrator on the office desktop and on a laptop in the van wants
 * different answers, and a setting stored against the user gives them one.
 */
const STORAGE_KEY = 'fdt.theme';

/** What the user picked, which is not the same as what is on screen. */
export type ThemePreference = ThemeMode | 'system';

/** What someone who has never touched the switcher gets. */
const DEFAULT_PREFERENCE: ThemePreference = 'dark';

interface ThemeModeValue {
  preference: ThemePreference;
  /** The theme actually in use, once `system` has been resolved. */
  mode: ThemeMode;
  setPreference: (preference: ThemePreference) => void;
}

const ThemeModeContext = createContext<ThemeModeValue | null>(null);

function isPreference(value: string | null): value is ThemePreference {
  return value === 'system' || (THEME_MODES as readonly string[]).includes(value ?? '');
}

function storedPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isPreference(stored) ? stored : DEFAULT_PREFERENCE;
  } catch {
    // Private windows and blocked site data: a theme is not worth failing over.
    return DEFAULT_PREFERENCE;
  }
}

const DARK_QUERY = '(prefers-color-scheme: dark)';

export function ThemeModeProvider({ children }: { children: ReactNode }) {
  const [preference, setStored] = useState<ThemePreference>(storedPreference);
  const [systemMode, setSystemMode] = useState<ThemeMode>(() =>
    window.matchMedia?.(DARK_QUERY).matches ? 'dark' : 'light',
  );

  // Followed for as long as `system` is the choice — someone who switches
  // their machine to dark at dusk should not have to come back here.
  useEffect(() => {
    const query = window.matchMedia?.(DARK_QUERY);
    if (!query) return undefined;

    const onChange = (event: MediaQueryListEvent) => setSystemMode(event.matches ? 'dark' : 'light');
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const setPreference = useCallback((next: ThemePreference) => {
    setStored(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Remembered for this tab at least.
    }
  }, []);

  const mode: ThemeMode = preference === 'system' ? systemMode : preference;

  // Tells the browser which scrollbars and form controls to draw, so the bits
  // of the page MUI does not paint match the rest of it.
  useEffect(() => {
    document.documentElement.style.colorScheme = mode;
  }, [mode]);

  const theme = useMemo(() => buildTheme(mode), [mode]);
  const value = useMemo(
    () => ({ preference, mode, setPreference }),
    [preference, mode, setPreference],
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}

export function useThemeMode(): ThemeModeValue {
  const value = useContext(ThemeModeContext);
  if (!value) {
    throw new Error('useThemeMode must be used inside ThemeModeProvider');
  }
  return value;
}
